import React from "react";
import { Link } from "react-router-dom";
import { LuCheck, LuArrowRight, LuSparkles } from "react-icons/lu";
import PublicNav, { PublicFooter } from "../components/PublicNav";
import SEO, { BASE_URL } from "../components/SEO";
import "./seoLanding.css";

/* Keyword-targeted SEO landing pages (one template, config-driven — mirrors the
   ElevateStaffing ModuleLandingPage pattern). Each has a single H1, keyword-rich
   H2s, SoftwareApplication + FAQPage JSON-LD, and an internal-link cluster to
   its siblings. */

export const LANDING = {
  "ai-resume-builder": {
    label: "AI Resume Builder",
    title: "AI Resume Builder",
    description:
      "Build a modern, ATS-friendly resume in minutes with CareerSavvy's AI resume builder — upload once and get an optimised, recruiter-ready resume.",
    h1: "AI Resume Builder — ATS-friendly resumes in minutes",
    lede: "Upload your resume once and let CareerSavvy's AI turn it into a clean, modern, ATS-friendly document that gets past screeners and in front of recruiters.",
    features: [
      { h: "ATS-optimised formatting", p: "A single-column, parser-safe layout with real headings so applicant tracking systems read every line." },
      { h: "AI content suggestions", p: "Concrete, role-relevant improvements to your summary, bullet points, and skills." },
      { h: "Instant PDF & Word export", p: "Download a crisp PDF or an editable Word document in one click." },
      { h: "Skills that stand out", p: "Skills organised into clean, scannable categories a recruiter can skim in seconds." },
    ],
    steps: [
      { h: "Upload your resume", p: "Drop in your current PDF or DOCX — we parse it automatically." },
      { h: "Review AI suggestions", p: "See what to strengthen and apply the changes you like." },
      { h: "Download & apply", p: "Export an ATS-friendly resume and start applying with confidence." },
    ],
    faqs: [
      { q: "Is the AI resume builder free?", a: "You can start for free; Pro unlocks tailored resumes and advanced suggestions." },
      { q: "Will my resume pass ATS?", a: "Yes — resumes use a single-column, parser-safe layout with standard section headings that applicant tracking systems read reliably." },
      { q: "Can I export to Word?", a: "Yes — download your resume as a PDF or an editable Word (.docx) file." },
    ],
  },
  "resume-tailoring": {
    label: "Resume Tailoring",
    title: "AI Resume Tailoring for Every Job",
    description:
      "Tailor your resume to any job description in seconds. CareerSavvy's AI matches your resume to the role and generates an ATS-friendly, job-specific version.",
    h1: "Tailor your resume to any job — automatically",
    lede: "Paste a job or pick a match, and CareerSavvy rewrites your resume to fit the role: the right keywords, the right emphasis, an ATS-friendly document ready to send.",
    features: [
      { h: "Job-specific keywords", p: "Your resume is aligned to the exact skills and terms the job description asks for." },
      { h: "See the gap first", p: "A clear before/after shows what the role wants versus what your resume says." },
      { h: "One-click tailored resume", p: "Generate a job-specific, ATS-friendly resume and download it instantly." },
      { h: "Never lose your base", p: "Your original resume stays intact — tailoring creates a new version each time." },
    ],
    steps: [
      { h: "Pick a job", p: "Choose a matched role or bring your own job description." },
      { h: "Review the changes", p: "Approve the AI's targeted edits for that specific job." },
      { h: "Download & apply", p: "Send a resume that speaks directly to the role." },
    ],
    faqs: [
      { q: "How does resume tailoring work?", a: "CareerSavvy compares your resume to the job description and rewrites the summary, bullets, and skills to match the role while keeping everything truthful." },
      { q: "Is the tailored resume ATS-friendly?", a: "Yes — every tailored resume uses a clean, single-column, parser-safe layout." },
      { q: "Does tailoring change my original resume?", a: "No — your base resume stays as-is; each tailored version is separate." },
    ],
  },
  "ai-job-search": {
    label: "AI Job Search",
    title: "AI Job Search & Matching",
    description:
      "Stop scrolling job boards. CareerSavvy's AI matches you to relevant jobs by reading your resume and scoring each role by fit — in the US and India.",
    h1: "AI job search that matches you to the right roles",
    lede: "CareerSavvy reads your resume and scores every job by how well you fit — so you spend time on the roles worth applying to, not endless scrolling.",
    features: [
      { h: "Personalised match scores", p: "Every job shows how well your skills and experience fit the role." },
      { h: "US & India job markets", p: "Relevant roles for where you're searching, with the right currency and context." },
      { h: "Apply smarter", p: "Tailor your resume to any match and practice for the interview — all in one place." },
      { h: "Less noise", p: "Fewer, better-fit applications beat spraying your resume everywhere." },
    ],
    steps: [
      { h: "Upload your resume", p: "We learn your skills, titles, and experience." },
      { h: "See scored matches", p: "Browse roles ranked by fit, not just recency." },
      { h: "Apply & prepare", p: "Tailor your resume and practice interviews for each role." },
    ],
    faqs: [
      { q: "How are job matches scored?", a: "CareerSavvy analyses your uploaded resume and scores each job by how closely your skills and experience match the requirements." },
      { q: "Do you support jobs outside the US?", a: "Yes — CareerSavvy supports both US and India job markets, with market-appropriate results." },
      { q: "Is it free to search jobs?", a: "You can start for free; Pro unlocks match analysis and resume tailoring." },
    ],
  },
  "mock-interview-practice": {
    label: "Mock Interviews",
    title: "Mock Interview Practice with AI",
    description:
      "Practice interviews with an AI coach: realistic questions, spoken answers and instant feedback, in text or voice mock interviews from CareerSavvy.",
    h1: "Mock interview practice with an AI coach",
    lede: "Rehearse real interview questions out loud and get instant, specific feedback — text and voice mock interviews that build your confidence before it counts.",
    features: [
      { h: "Realistic questions", p: "Questions tailored to your resume and target role, not generic lists." },
      { h: "Speak your answers", p: "Answer by voice, just like a real interview, and get a transcript." },
      { h: "Instant AI feedback", p: "See what worked and what to improve after every answer." },
      { h: "Text & video modes", p: "Practice by chat, by voice, or in a full video interview." },
    ],
    steps: [
      { h: "Choose a focus", p: "Pick the role or topic you want to practice." },
      { h: "Answer out loud", p: "Respond to AI questions by voice or text." },
      { h: "Review feedback", p: "Get a score and concrete tips to improve." },
    ],
    faqs: [
      { q: "Are the mock interviews realistic?", a: "Yes — questions are tailored to your resume and role, and you can answer by voice with live transcription." },
      { q: "Do I get feedback?", a: "Yes — you get instant, specific feedback and a score after your answers." },
      { q: "Can I practice video interviews?", a: "Yes — CareerSavvy includes text, voice, and full video interview practice." },
    ],
  },
  "interview-preparation": {
    label: "Interview Prep",
    title: "AI Interview Preparation",
    description:
      "Prepare for your interview with CareerSavvy — role-specific questions, coding practice, and AI feedback so you walk in ready and confident.",
    h1: "AI interview preparation, tailored to your role",
    lede: "Get ready for your next interview with role-specific questions, coding practice, and AI feedback — everything you need to walk in prepared.",
    features: [
      { h: "Role-specific prep", p: "Practice the questions that actually come up for your target role." },
      { h: "Coding practice", p: "Work through problems in an in-browser editor for technical rounds." },
      { h: "AI feedback", p: "Understand your strengths and gaps with clear, actionable feedback." },
      { h: "Practice on your time", p: "Prepare whenever you want, as many times as you need." },
    ],
    steps: [
      { h: "Pick your role", p: "Tell us the role you're interviewing for." },
      { h: "Practice questions", p: "Work through tailored behavioural and technical questions." },
      { h: "Refine with feedback", p: "Use AI feedback to sharpen your answers." },
    ],
    faqs: [
      { q: "What does interview prep include?", a: "Role-specific behavioural and technical questions, in-browser coding practice, and AI feedback on your answers." },
      { q: "Is coding practice included?", a: "Yes — you can practice coding problems in an in-browser editor." },
      { q: "Is interview prep free?", a: "You can start for free; Pro unlocks the full experience." },
    ],
  },
};

export default function SeoLanding({ slug }) {
  const cfg = LANDING[slug];
  if (!cfg) return null;
  const path = `/${slug}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `CareerSavvy — ${cfg.title}`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: cfg.description,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: "Free to start" },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: cfg.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const related = Object.entries(LANDING).filter(([s]) => s !== slug);

  return (
    <div className="csx">
      <SEO
        title={cfg.title}
        description={cfg.description}
        canonicalPath={path}
        jsonLd={jsonLd}
        breadcrumbs={[{ label: cfg.label, path }]}
      />
      <div className="csx-page">
        <PublicNav />
        <main>
          {/* hero */}
          <section className="land-section sl-hero">
            <div className="wrap">
              <span className="sl-eyebrow"><LuSparkles size={14} /> CareerSavvy</span>
              <h1 className="sl-h1">{cfg.h1}</h1>
              <p className="sl-lede">{cfg.lede}</p>
              <div className="sl-cta">
                <Link to="/page-register" className="btn btn-pill btn-mint btn-lg">
                  Get started free <LuArrowRight size={16} />
                </Link>
                <Link to="/login" className="btn btn-pill btn-ghost btn-lg">Sign in</Link>
              </div>
            </div>
          </section>

          {/* features */}
          <section className="land-section">
            <div className="wrap">
              <h2 className="sl-h2">What you get</h2>
              <div className="sl-grid">
                {cfg.features.map((f) => (
                  <div className="sl-card" key={f.h}>
                    <span className="sl-card-ic"><LuCheck size={16} /></span>
                    <div>
                      <h3 className="sl-card-h">{f.h}</h3>
                      <p className="sl-card-p">{f.p}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* how it works */}
          <section className="land-section sl-alt">
            <div className="wrap">
              <h2 className="sl-h2">How it works</h2>
              <ol className="sl-steps">
                {cfg.steps.map((s, i) => (
                  <li key={s.h}>
                    <span className="sl-step-n">{i + 1}</span>
                    <div>
                      <h3 className="sl-card-h">{s.h}</h3>
                      <p className="sl-card-p">{s.p}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* faq */}
          <section className="land-section">
            <div className="wrap">
              <h2 className="sl-h2">Frequently asked questions</h2>
              <div className="sl-faq">
                {cfg.faqs.map((f) => (
                  <div className="sl-faq-item" key={f.q}>
                    <h3 className="sl-faq-q">{f.q}</h3>
                    <p className="sl-card-p">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* related tools (internal-link cluster) */}
          <section className="land-section sl-alt">
            <div className="wrap">
              <h2 className="sl-h2">Explore more</h2>
              <div className="sl-related">
                {related.map(([s, c]) => (
                  <Link key={s} to={`/${s}`} className="sl-related-link">
                    {c.label} <LuArrowRight size={14} />
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* cta */}
          <section className="land-section sl-cta-band">
            <div className="wrap">
              <h2 className="sl-h2">Ready to land your next role?</h2>
              <p className="sl-lede">Start free — upload your resume and see your matches, tailored resumes, and interview practice in minutes.</p>
              <Link to="/page-register" className="btn btn-pill btn-mint btn-lg">
                Create your free account <LuArrowRight size={16} />
              </Link>
            </div>
          </section>
        </main>
        <PublicFooter />
      </div>
    </div>
  );
}
